import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import LogoSVG from './LogoSVG';

const navItems = [
  { name: 'Accueil', href: '#hero' },
  { name: 'Offres', href: '#services' },
  { name: 'Stack', href: '#technologies' },
  { name: 'Projets', href: '#projects' },
];

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const sectionIds = navItems.map((item) => item.href.substring(1));

    const handleScroll = () => {
      const currentSection = sectionIds.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobileMenuOpen]);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(href.substring(1));
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 py-6"
      aria-label="Navigation principale"
    >
      <div className="max-w-[1440px] mx-auto px-8 sm:px-12 lg:px-16">
        <div className="flex items-center justify-between">
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex-shrink-0 cursor-pointer -ml-3 h-12 bg-transparent border-0 p-0"
            onClick={() => scrollToSection('#hero')}
            aria-label="Retour à l'accueil"
          >
            <LogoSVG />
          </motion.button>

          <div className="hidden md:flex flex-1 justify-center">
            <div className="inline-flex items-center bg-white shadow-sm border border-gray-200 rounded-full px-2 py-1.5">
              {navItems.map((item) => (
                <motion.button
                  key={item.name}
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToSection(item.href)}
                  aria-current={activeSection === item.href.substring(1) ? 'page' : undefined}
                  className="relative flex items-center px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-200 group"
                >
                  <span
                    className={`absolute left-2.5 w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                      activeSection === item.href.substring(1)
                        ? 'bg-[#FFE600]'
                        : 'bg-gray-300 group-hover:bg-gray-400'
                    }`}
                    aria-hidden
                  />
                  <span
                    className={`ml-3 transition-colors duration-200 ${
                      activeSection === item.href.substring(1)
                        ? 'text-gray-900'
                        : 'text-gray-600 group-hover:text-gray-900'
                    }`}
                  >
                    {item.name}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          <div className="hidden md:block mr-3">
            <motion.button
              type="button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('#contact')}
              className="px-8 py-3.5 text-sm font-medium text-black bg-[#FFE600] rounded-full hover:bg-[#FFE600]/90 transition-all duration-200"
            >
              Contact / Devis
            </motion.button>
          </div>

          <div className="md:hidden">
            <motion.button
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-gray-900"
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isMobileMenuOpen ? 'close' : 'open'}
                  initial={{ rotate: -180, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 180, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {isMobileMenuOpen ? (
                    <XMarkIcon className="h-6 w-6" aria-hidden />
                  ) : (
                    <Bars3Icon className="h-6 w-6" aria-hidden />
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              id="mobile-menu"
              role="menu"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden"
            >
              <motion.div className="px-2 pt-2 pb-3 space-y-1 bg-white rounded-2xl mt-4 shadow-lg border border-gray-100">
                {navItems.map((item) => (
                  <motion.button
                    key={item.name}
                    type="button"
                    role="menuitem"
                    whileHover={{ x: 10 }}
                    onClick={() => scrollToSection(item.href)}
                    className="relative flex items-center w-full text-left px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group"
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                        activeSection === item.href.substring(1)
                          ? 'bg-[#FFE600]'
                          : 'bg-gray-300 group-hover:bg-gray-400'
                      }`}
                      aria-hidden
                    />
                    <span
                      className={`ml-3 transition-colors duration-200 ${
                        activeSection === item.href.substring(1)
                          ? 'text-gray-900'
                          : 'text-gray-600 group-hover:text-gray-900'
                      }`}
                    >
                      {item.name}
                    </span>
                  </motion.button>
                ))}
                <div className="pt-2 px-2">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => scrollToSection('#contact')}
                    className="w-full py-3 text-sm font-medium text-black bg-[#FFE600] rounded-xl hover:bg-[#FFE600]/90 transition-all duration-200"
                  >
                    Contact / Devis
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
